import { Divider, Group, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { ColDef } from "ag-grid-community";
import * as R from "ramda";
import React from "react";
import ViewEditTable from "../../../../../components/ViewEditTable/ViewEditTable";
import {
  IElektroSamsvarserklaringFELRapport,
  INormOrStandardTableRow,
  ISpesificationTableRow,
} from "../../../../../mc-shared/reportSchemas/elektroSamsvarserklaringFELSchema";
import { dividerWeight, labelWeight } from "../../reportBoardConstants";
import "./RapportSamsvarserklaring.css";

interface ISpecification {
  spesifikasjonsId: string;
  spesifikasjonsType: string;
  argang: string;
}

export interface INormEllerStandard {
  normEllerStandard: string;
  utgave: string;
}

const RapportSamsvarserklaringFEL: React.FC<{
  samsvarserklaring: IElektroSamsvarserklaringFELRapport;
  onUpdateEditData: (data: IElektroSamsvarserklaringFELRapport) => void;
}> = ({ samsvarserklaring, onUpdateEditData }) => {
  const [editData, setEditData] = React.useState<IElektroSamsvarserklaringFELRapport>(R.clone(samsvarserklaring));

  React.useEffect(() => {
    onUpdateEditData(editData);
  }, [editData]);

  React.useEffect(() => {
    setEditData(null);
    setTimeout(() => {
      setEditData(samsvarserklaring);
    }, 50);
  }, [samsvarserklaring]);

  const columns: ColDef<ISpesificationTableRow>[] = [
    {
      field: "spesifikasjonsId",
      headerName: "Id",
      editable: true,
      width: 60,
    },
    {
      field: "spesifikasjonsType",
      headerName: "Navn",
      editable: true,
      flex: 1,
    },
    {
      field: "argang",
      headerName: "Årgang",
      editable: true,
      flex: 1,
    },
  ];

  const utfoertIhtColumns: ColDef<INormOrStandardTableRow>[] = [
    {
      field: "normEllerStandard",
      headerName: "Norm eller standard",
      editable: true,
      flex: 1,
    },
    {
      field: "utgave",
      headerName: "Utgave",
      editable: true,
      flex: 1,
    },
  ];

  const newItemTemplateObject = {
    spesifikasjonsId: "",
    spesifikasjonsType: "",
    argang: "",
  };

  const utfoertIhtNewItemTemplate = {
    normEllerStandard: "",
    utgave: "",
  };

  const updateData = (key: string, value: string | ISpesificationTableRow[] | INormEllerStandard[]) => {
    const updated = R.assocPath(key.split("."), value, editData);
    setEditData({
      ...editData,
      ...updated,
    });
  };

  const updateAndreTekniskeSpesifikasjoner = (viewTableData: ISpecification[]): void => {
    const convertDataToISpesificationTableRows: ISpesificationTableRow[] = viewTableData?.map((data) => {
      return {
        spesifikasjonsId: data.spesifikasjonsId,
        spesifikasjonsType: data.spesifikasjonsType,
        argang: data.argang,
      };
    });
    updateData("andreTekniskeSpesifikasjoner", convertDataToISpesificationTableRows);
  };

  const updateUtfoertIht = (viewTableData: INormEllerStandard[]): void => {
    updateData("anvendteNormerOgStandarder", viewTableData);
  };

  return (
    <Stack p="md" style={{ maxWidth: 700, margin: "0 auto" }}>
      {editData != null && (
        <>
          <Text size="xl" align="center" fw={500}>
            Samsvarserklæring FEL:
          </Text>
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Generell info
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>Utarbeidet av:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.utarbeidetAv}
                onChange={(e) => updateData("generellInfo.utarbeidetAv", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Kontrollert av:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.kontrollertAv}
                onChange={(e) => updateData("generellInfo.kontrollertAv", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Godkjent av:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.godkjentAv}
                onChange={(e) => updateData("generellInfo.godkjentAv", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Dokumentnummer:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.dokumentNr}
                onChange={(e) => updateData("generellInfo.dokumentNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Revisjonsnummer:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.revisjonsNr}
                onChange={(e) => updateData("generellInfo.revisjonsNr", e.target.value)}
              />
            </Group>
          </Stack>
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Opplysninger om prosjekterende
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>1.1 Prosjekterende:</Text>
              <TextInput
                size="sm"
                value={editData.prosjekterende?.navn}
                onChange={(e) => updateData("prosjekterende.navn", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>1.2 Kontaktperson:</Text>
              <TextInput
                size="sm"
                value={editData.prosjekterende?.kontaktPerson}
                onChange={(e) => updateData("prosjekterende.kontaktPerson", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>1.3 Adresse:</Text>
              <TextInput
                size="sm"
                value={editData.prosjekterende?.adresse}
                onChange={(e) => updateData("prosjekterende.adresse", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>1.4 Postnummer:</Text>
              <TextInput
                size="sm"
                value={editData.prosjekterende?.postNr}
                onChange={(e) => updateData("prosjekterende.postNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>1.5 Sted:</Text>
              <TextInput
                size="sm"
                value={editData.prosjekterende?.sted}
                onChange={(e) => updateData("prosjekterende.sted", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>1.6 Telefon:</Text>
              <TextInput
                size="sm"
                value={editData.prosjekterende?.telefon}
                onChange={(e) => updateData("prosjekterende.telefon", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>1.7 E-post:</Text>
              <TextInput
                size="sm"
                value={editData.prosjekterende?.email}
                onChange={(e) => updateData("prosjekterende.email", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>1.8 Oppdragsnummer:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsNummer}
                onChange={(e) => updateData("oppdragsInfo.oppdragsNummer", e.target.value)}
              />
            </Group>
          </Stack>
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Opplysninger om oppdragsgiver/kunde
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>2.1 Oppdragsgiver:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.navn}
                onChange={(e) => updateData("oppdragsInfo.oppdragsGiver.navn", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>2.2 Kontaktperson:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.kontaktPerson}
                onChange={(e) => updateData("oppdragsInfo.oppdragsGiver.kontaktPerson", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>2.3 Adresse:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.adresse}
                onChange={(e) => updateData("oppdragsInfo.oppdragsGiver.adresse", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>2.4 Postnummer:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.postNr}
                onChange={(e) => updateData("oppdragsInfo.oppdragsGiver.postNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>2.5 Sted:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.sted}
                onChange={(e) => updateData("oppdragsInfo.oppdragsGiver.sted", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>2.6 Telefon:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.telefon}
                onChange={(e) => updateData("oppdragsInfo.oppdragsGiver.telefon", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>2.7 E-post:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.email}
                onChange={(e) => updateData("oppdragsInfo.oppdragsGiver.email", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>2.8 Kundenummer / Prosjektnummer:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsInfo?.oppdragsGiver?.kundenummerEllerProsjektnummer}
                onChange={(e) =>
                  updateData("oppdragsInfo.oppdragsGiver.kundenummerEllerProsjektnummer", e.target.value)
                }
              />
            </Group>
          </Stack>
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Opplysninger om anlegget/prosjektet
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>3.1 Anleggsnavn:</Text>
              <TextInput
                size="sm"
                value={editData.anleggEllerprosjekt?.anleggsnavnEllerProsjektnavn}
                onChange={(e) => updateData("anleggEllerprosjekt.anleggsnavnEllerProsjektnavn", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>3.2 Adresse:</Text>
              <TextInput
                size="sm"
                value={editData.anleggEllerprosjekt?.adresse}
                onChange={(e) => updateData("anleggEllerprosjekt.adresse", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>3.3 Postnummer:</Text>
              <TextInput
                size="sm"
                value={editData.anleggEllerprosjekt?.postNr}
                onChange={(e) => updateData("anleggEllerprosjekt.postNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>3.4 Sted:</Text>
              <TextInput
                size="sm"
                value={editData.anleggEllerprosjekt?.sted}
                onChange={(e) => updateData("anleggEllerprosjekt.sted", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>3.5 SSB-nr:</Text>
              <TextInput
                size="sm"
                value={editData.anleggEllerprosjekt?.SSBnr}
                onChange={(e) => updateData("anleggEllerprosjekt.SSBnr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>3.6 Type anlegg:</Text>
              <TextInput
                size="sm"
                value={editData.anleggEllerprosjekt?.typeAnlegg}
                onChange={(e) => updateData("anleggEllerprosjekt.typeAnlegg", e.target.value)}
              />
            </Group>
          </Stack>
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Utført i henhold til
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <ViewEditTable
            data={editData.anvendteNormerOgStandarder}
            columns={utfoertIhtColumns}
            onSave={updateUtfoertIht}
            newItemTemplateObject={utfoertIhtNewItemTemplate}
            canEdit={true}
          />
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Beskrivelse av fravik
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Textarea
            minRows={2}
            autosize
            value={editData.fravikFraAnvendteNormer}
            onChange={(e) => updateData("fravikFraAnvendteNormer", e.target.value)}
            placeholder="Skriv en beskrivelse av fravik her..."
          />
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Andre tekniske spesifikasjoner
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <ViewEditTable
            data={editData.andreTekniskeSpesifikasjoner}
            columns={columns}
            onSave={updateAndreTekniskeSpesifikasjoner}
            newItemTemplateObject={newItemTemplateObject}
            canEdit={true}
          />
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Beskrivelse av oppdraget
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Textarea
            minRows={2}
            autosize
            value={editData.beskrivelseAvOppdrag}
            onChange={(e) => updateData("beskrivelseAvOppdrag", e.target.value)}
            placeholder="Skriv en beskrivelse av oppdraget her..."
          />
          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Underlagsdokumentasjon (FEL §12)
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>8.1 Dokumentplan:</Text>
              <TextInput
                size="sm"
                value={editData.underlagsdokumentasjon?.dokumentplan}
                onChange={(e) => updateData("underlagsdokumentasjon.dokumentplan", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>8.2 Fordelingsskjema:</Text>
              <TextInput
                size="sm"
                value={editData.underlagsdokumentasjon?.fordelingsskjema}
                onChange={(e) => updateData("underlagsdokumentasjon.fordelingsskjema", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>8.3 Beskrivelser:</Text>
              <TextInput
                size="sm"
                value={editData.underlagsdokumentasjon?.beskrivelser}
                onChange={(e) => updateData("underlagsdokumentasjon.beskrivelser", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>8.4 Resultat brukermøter:</Text>
              <TextInput
                size="sm"
                value={editData.underlagsdokumentasjon?.resultatFraBrukermoeter}
                onChange={(e) => updateData("underlagsdokumentasjon.resultatFraBrukermoeter", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>8.5 Elektrotekniske beregninger:</Text>
              <TextInput
                size="sm"
                value={editData.underlagsdokumentasjon?.elektrotekniskeBeregninger}
                onChange={(e) => updateData("underlagsdokumentasjon.elektrotekniskeBeregninger", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>8.6 Risikovurdering:</Text>
              <TextInput
                size="sm"
                value={editData.underlagsdokumentasjon?.risikoburdering}
                onChange={(e) => updateData("underlagsdokumentasjon.risikoburdering", e.target.value)}
              />
            </Group>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default RapportSamsvarserklaringFEL;
